import { LAST_UPDATED } from './legalMeta'

/**
 * Grievance Redressal.
 *
 * Required disclosure: the Consumer Protection (E-Commerce) Rules, 2020 oblige
 * an e-commerce entity to appoint a Grievance Officer, display their name and
 * contact details on the consumer-facing platform, acknowledge a complaint
 * within 48 hours and redress it within one month. The Digital Personal Data
 * Protection Act, 2023 requires a published route for data grievances.
 *
 * ACTION FOR THE ADMIN: the officer's real name and designation must be filled
 * in. This page reads `grievance_officer_name` and `grievance_email` from
 * `GET /general-settings` and falls back to the generic "The Grievance Officer"
 * plus the support email until those fields are set.
 */
export const GRIEVANCE_REDRESSAL = {
  slug: 'grievance-redressal',
  badge: 'Legal',
  title: 'Grievance Redressal',
  intro:
    'If something has gone wrong, this page tells you exactly who to write to, what to include, how long we will take, and where to go if we still have not put it right.',
  updated: LAST_UPDATED,
  summary: [
    'Start with support — most problems are solved there within a day or two.',
    'Not resolved? Escalate to our Grievance Officer, named in section 3.',
    'We acknowledge every complaint within 48 hours and give you a reference number.',
    'We work to resolve a complaint within one month, as the Consumer Protection (E-Commerce) Rules, 2020 require.',
    'If you are still unsatisfied, you can approach a Consumer Commission or, for a data complaint, the Data Protection Board of India.',
  ],
  sections: [
    {
      heading: '1. What this page is for',
      body: [
        'We would always rather hear a complaint than lose your trust quietly. This page is the formal route for anything our support team has not been able to settle — a booking, an order, a refund, a course, the conduct of an astrologer or instructor, content on our site, or the way we have handled your personal data.',
        '{company} publishes this process to meet its obligations under the Consumer Protection Act, 2019 and the Consumer Protection (E-Commerce) Rules, 2020, the Information Technology Act, 2000 and the rules made under it, and the Digital Personal Data Protection Act, 2023.',
      ],
    },
    {
      heading: '2. Step one: talk to support',
      body: [
        'Please start here, because it is usually the fastest fix. Reach us{contact}, or raise a support request from your dashboard, and quote your booking, order or enrolment reference.',
        'Support acknowledges a request within 48 hours and ordinarily answers a straightforward question within 2 working days. For cancellations, returns and refunds, the timelines and outcomes in our Cancellation & Refund Policy apply.',
      ],
    },
    {
      heading: '3. Step two: escalate to the Grievance Officer',
      body: [
        'If support has not resolved the matter, or you are not satisfied with the outcome, escalate it to our Grievance Officer.',
      ],
      list: [
        'Grievance Officer: {officer}',
        'Email: {gemail}',
        'Phone: {phone}',
        'Postal address: {address}',
        'Working hours: Monday to Saturday, excluding public holidays. Please allow for Indian Standard Time.',
      ],
      note: 'Please write to the Grievance Officer only after support has had a chance to help, and include the earlier reference number — it lets the officer see the whole history and respond properly rather than starting over.',
    },
    {
      heading: '4. What to include in your complaint',
      body: [
        'A complaint that has these details can usually be decided without us having to come back to you for more.',
      ],
      list: [
        'Your name, and the email address and mobile number registered with us.',
        'The booking, order, enrolment, invoice or certificate reference the complaint concerns.',
        'What happened, with dates — and the names of anyone you spoke to.',
        'What you are asking for — a refund, a replacement, a re-delivery, a repeat session, a correction, a deletion, or an explanation.',
        'Supporting material — screenshots, photographs of a damaged item and its packing, a payment reference, or copies of earlier correspondence.',
        'For a data complaint, which right under our Privacy Policy you are exercising — access, correction, erasure, withdrawal of consent, or nomination.',
      ],
    },
    {
      heading: '5. Our timelines',
      list: [
        'Acknowledgement — within 48 hours of receiving your complaint, with a reference number you can quote to track it.',
        'Resolution — within one month of receiving it, and sooner wherever we can.',
        'Unlawful content — a complaint that content on our platform is unlawful is acted on as expeditiously as possible, and ordinarily within 15 days of receipt.',
        'Data rights requests — ordinarily within 30 days, as our Privacy Policy sets out.',
        'If we need more information from you, we will ask within the acknowledgement period, and the clock continues once you reply.',
        'We will tell you our decision in writing, with the reasons for it, whether or not it goes your way.',
      ],
    },
    {
      heading: '6. How we handle a complaint',
      body: [
        'The officer reviews the records for your booking or order, the correspondence, the payment and shipment history, and where relevant speaks to the astrologer, instructor, temple or courier involved. We keep a record of every complaint, what was decided and why.',
        'We will not penalise you for complaining, and we will not close your account or withdraw a service because you raised a grievance or exercised a right.',
      ],
    },
    {
      heading: '7. If you are still not satisfied',
      body: [
        'Our process does not replace your legal remedies. You are free to approach any of the following at any time, and you do not need our permission to do so.',
      ],
      list: [
        'A Consumer Commission — the District, State or National Consumer Disputes Redressal Commission that has jurisdiction, including where you reside, under the Consumer Protection Act, 2019.',
        'The National Consumer Helpline — run by the Department of Consumer Affairs, Government of India, which also offers online complaint registration.',
        'The Data Protection Board of India — for a complaint about how we have handled your personal data, once the Board is constituted and operational under the Digital Personal Data Protection Act, 2023, and after you have first raised it with us.',
        'Your bank or card issuer — for a disputed transaction, through its own chargeback process; please tell us too, so we can supply the records.',
        'Cyber Crime reporting — for fraud or impersonation involving our name, at the Government of India cybercrime portal and your local police.',
      ],
    },
    {
      heading: '8. Reporting fraud and impersonation',
      body: [
        'Fake pages, cloned WhatsApp numbers and people claiming to be our astrologers are a real problem in this industry. We will never ask you for your card number, CVV, UPI PIN, OTP or net-banking password, we will never ask you to install an app or scan a QR code to receive a refund, and we will never ask you to pay an astrologer directly to a personal account.',
        'If someone does any of those in our name, do not pay. Report it to us{contact} with screenshots and the number or link used, and we will act on it and help you report it to the authorities.',
      ],
    },
    {
      heading: '9. Changes to this page',
      body: [
        'We will update this page if the officer, the contact details or the applicable timelines change. The "Last updated" date at the top tells you when it last changed.',
      ],
    },
  ],
}
